import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../api/api';
import './WellDashboard.css';

// Template processing utility
const processTemplate = (template, data) => {
  if (!template || !data) return '';
  
  return template.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
    const value = data[key];
    if (value === undefined || value === null) return '—';
    
    // Handle conditional logic for plurals
    if (key === 'recordCount !== 1') {
      return data.recordCount !== 1 ? 's' : '';
    }
    
    return value;
  });
};

// Format file size utility
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Format date utility
const formatDate = (dateString) => {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return '—';
  }
};

// Data Card Component
const DataCard = ({ section, data, onRefresh, onViewDetails }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleRefresh = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await onRefresh(section.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const processedTitle = processTemplate(section.titleTemplate, data);
  const processedSummary = processTemplate(section.summaryTemplate, data);

  return (
    <div className="data-card" data-style={section.id}>
      <div className="card-header">
        <h3 className="card-title">
          <span className="card-icon">{section.icon}</span>
          {processedTitle || section.name}
        </h3>
        <div className={`card-status ${isLoading ? 'loading' : error ? 'error' : 'success'}`}>
          {isLoading ? 'Loading...' : error ? 'Error' : 'Ready'}
        </div>
      </div>
      
      <div className="card-content">
        {error && (
          <div className="error-message">
            Error loading data: {error}
          </div>
        )}
        
        {!error && (
          <div className="card-summary">
            {processedSummary.split('\n').map((line, index) => {
              const [label, value] = line.split(': ');
              if (!value) return null;
              
              return (
                <div key={index} className="summary-line">
                  <span className="summary-label">{label}:</span>
                  <span className={`summary-value ${index === 0 ? 'highlight' : ''}`}>
                    {value}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        
        <div className="card-actions">
          <button 
            className="action-button" 
            onClick={handleRefresh}
            disabled={isLoading}
          >
            {isLoading ? <span className="loading-spinner"></span> : '🔄'} Refresh
          </button>
          <button 
            className="action-button primary" 
            onClick={() => onViewDetails(section)}
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default function WellDashboard() {
  const navigate = useNavigate();
  const { id: wellId } = useParams(); // Get wellId from URL
  const [config, setConfig] = useState(null);
  const [sectionsData, setSectionsData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');

  // Load configuration
  const loadConfiguration = useCallback(async () => {
    try {
      const configData = await apiFetch('/api/welldata/config');
      setConfig(configData);
      return configData;
    } catch (err) {
      console.error('Failed to load WellDashboard configuration:', err);
      setError('Failed to load dashboard configuration');
      return null;
    }
  }, []);

  // Load data for a specific section
  const loadSectionData = useCallback(async (sectionId, sectionConfig) => {
    try {
      let data = {};
      const wellFilter = wellId ? `?wellId=${wellId}` : '';
      
      // Load data based on section type
      switch (sectionId) {
        case 'well':
          const wellsEndpoint = wellId ? `/api/wells/${wellId}` : '/api/db/Well';
          const wells = await apiFetch(wellsEndpoint);
          const wellsArray = Array.isArray(wells) ? wells : [wells];
          data = {
            recordCount: wellsArray.length,
            activeCount: wellsArray.filter(w => w.WellStatus === 'Active').length,
            recentCount: wellsArray.filter(w => {
              const createdDate = new Date(w.CreatedAt);
              const thirtyDaysAgo = new Date();
              thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
              return createdDate > thirtyDaysAgo;
            }).length
          };
          break;
          
        case 'dailyreports':
          const reports = await apiFetch(`/api/ddr${wellFilter}`);
          const uniqueWells = new Set(reports?.map(r => r.WellID).filter(Boolean));
          const latestReport = reports?.length ? reports.sort((a, b) => new Date(b.ReportDate) - new Date(a.ReportDate))[0] : null;
          data = {
            recordCount: reports?.length || 0,
            wellCount: uniqueWells.size,
            latestDate: latestReport ? formatDate(latestReport.ReportDate) : '—'
          };
          break;
          
        case 'deviation':
          try {
            // Try to get all deviation data
            const deviationData = await apiFetch(`/api/db/DeviationSurvey${wellFilter}`);
            const uniqueWellIds = new Set(deviationData?.map(d => d.WellID).filter(Boolean));
            const depths = deviationData?.map(d => d.MD).filter(d => d != null && !isNaN(d)) || [];
            data = {
              recordCount: deviationData?.length || 0,
              wellCount: uniqueWellIds.size,
              minDepth: depths.length ? Math.min(...depths).toFixed(0) : '—',
              maxDepth: depths.length ? Math.max(...depths).toFixed(0) : '—'
            };
          } catch (err) {
            console.warn('Deviation data not available:', err);
            data = { recordCount: 0, wellCount: 0, minDepth: '—', maxDepth: '—' };
          }
          break;
          
        case 'bha':
          try {
            const bhaData = await apiFetch(`/api/db/BHA${wellFilter}`);
            const bhaComponents = await apiFetch('/api/db/BHA_Component'); // Assuming components are linked by BHAID, not wellID directly
            const uniqueWellIds = new Set(bhaData?.map(b => b.WellID).filter(Boolean));
            data = {
              recordCount: bhaData?.length || 0,
              componentCount: bhaComponents?.length || 0,
              wellCount: uniqueWellIds.size
            };
          } catch (err) {
            console.warn('BHA data not available:', err);
            data = { recordCount: 0, componentCount: 0, wellCount: 0 };
          }
          break;
          
        case 'attachments':
          try {
            const attachments = await apiFetch(`/api/attachments${wellFilter}`);
            const totalSize = attachments?.reduce((sum, att) => sum + (att.FileSize || 0), 0) || 0;
            const fileTypes = new Set(attachments?.map(att => att.FileType).filter(Boolean));
            data = {
              recordCount: attachments?.length || 0,
              totalSize: formatFileSize(totalSize),
              fileTypes: fileTypes.size > 0 ? Array.from(fileTypes).slice(0, 3).join(', ') + (fileTypes.size > 3 ? '...' : '') : '—'
            };
          } catch (err) {
            console.warn('Attachments data not available:', err);
            data = { recordCount: 0, totalSize: '0 B', fileTypes: '—' };
          }
          break;
          
        case 'pobdata':
          try {
            const pobData = await apiFetch(`/api/db/POB${wellFilter}`);
            const latestPOB = pobData?.length ? pobData.sort((a, b) => new Date(b.ReportDate) - new Date(a.ReportDate))[0] : null;
            const totalPersonnel = pobData?.reduce((sum, pob) => sum + (pob.PersonnelCount || 0), 0) || 0;
            data = {
              recordCount: pobData?.length || 0,
              latestDate: latestPOB ? formatDate(latestPOB.ReportDate) : '—',
              totalPersonnel
            };
          } catch (err) {
            console.warn('POB data not available:', err);
            data = { recordCount: 0, latestDate: '—', totalPersonnel: 0 };
          }
          break;
          
        default:
          data = { recordCount: 0 };
      }
      
      setSectionsData(prev => ({
        ...prev,
        [sectionId]: {
          ...data,
          lastUpdated: new Date().toISOString()
        }
      }));
      
    } catch (err) {
      console.error(`Failed to load data for section ${sectionId}:`, err);
      setSectionsData(prev => ({
        ...prev,
        [sectionId]: {
          recordCount: 0,
          error: err.message,
          lastUpdated: new Date().toISOString()
        }
      }));
    }
  }, [wellId]);

  // Load all data
  const loadAllData = useCallback(async (configData) => {
    if (!configData?.sections) return;
    
    const enabledSections = configData.sections.filter(s => s.enabled);
    const loadPromises = enabledSections.map(section => 
      loadSectionData(section.id, section)
    );
    
    await Promise.allSettled(loadPromises);
    setLastUpdated(new Date());
  }, [loadSectionData]);

  // Initial load
  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    const initializeData = async () => {
      setIsLoading(true);
      setError(null);
      
      const configData = await loadConfiguration();
      if (configData) {
        await loadAllData(configData);
      }
      
      setIsLoading(false);
    };

    initializeData();
  }, [token, navigate, loadConfiguration, loadAllData, wellId]);

  // Refresh specific section
  const handleRefreshSection = useCallback(async (sectionId) => {
    if (!config) return;
    const section = config.sections.find(s => s.id === sectionId);
    if (section) {
      await loadSectionData(sectionId, section);
      setLastUpdated(new Date());
    }
  }, [config, loadSectionData]);

  // Refresh all data
  const handleRefreshAll = useCallback(async () => {
    if (!config) return;
    setIsLoading(true);
    await loadAllData(config);
    setIsLoading(false);
  }, [config, loadAllData]);

  // Handle view details
  const handleViewDetails = useCallback((section) => {
    // Navigate to relevant page based on section type
    switch (section.id) {
      case 'well':
        navigate(wellId ? `/welldetail/${wellId}` : '/');
        break;
      case 'dailyreports':
        navigate(wellId ? `/ddr?wellId=${wellId}` : '/daily-report');
        break;
      case 'deviation':
        navigate(wellId ? `/deviation-v2?wellId=${wellId}` : '/deviation');
        break;
      case 'attachments':
        navigate(wellId ? `/attachments?wellId=${wellId}` : '/attachments');
        break;
      case 'pobdata':
        navigate(wellId ? `/pob?wellId=${wellId}` : '/pob');
        break;
      default:
        console.log(`No specific page for ${section.id}`);
    }
  }, [navigate, wellId]);

  // Handle configuration management
  const handleOpenConfig = useCallback(() => {
    // For now, just log - could open a modal or navigate to config page
    console.log('Open WellDashboard configuration');
    // navigate('/admin/welldashboard-config'); // Future implementation
  }, []);

  if (!token) return null;

  if (isLoading && !config) {
    return (
      <div className="welldashboard-container">
        <div className="welldashboard-header">
          <div>
            <h1>Loading Dashboard...</h1>
          </div>
        </div>
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="welldashboard-container">
        <div className="error-message">
          {error}
        </div>
      </div>
    );
  }

  const enabledSections = config?.sections?.filter(s => s.enabled)?.sort((a, b) => a.order - b.order) || [];

  return (
    <div className="welldashboard-container">
      <div className="welldashboard-header">
        <div>
          <h1>{config?.globalSettings?.title || 'Well Data Dashboard'}</h1>
          <p className="subtitle">{config?.globalSettings?.subtitle || 'System Overview and Statistics'}</p>
          {lastUpdated && config?.globalSettings?.showLastUpdated && (
            <p className="last-updated">
              Last updated: {lastUpdated.toLocaleString()}
            </p>
          )}
        </div>
        <div className="actions">
          <button 
            className="refresh-button" 
            onClick={handleRefreshAll}
            disabled={isLoading}
          >
            {isLoading ? <span className="loading-spinner"></span> : '🔄'} 
            Refresh All
          </button>
          {(role === 'admin') && (
            <button 
              className="config-button" 
              onClick={handleOpenConfig}
            >
              ⚙️ Configure
            </button>
          )}
        </div>
      </div>

      {enabledSections.length === 0 && (
        <div className="empty-state">
          <h3>No Data Sections Configured</h3>
          <p>Please configure data sections to display on this dashboard.</p>
        </div>
      )}

      {enabledSections.length > 0 && (
        <div className="welldashboard-grid">
          {enabledSections.map(section => (
            <DataCard
              key={section.id}
              section={section}
              data={sectionsData[section.id] || { recordCount: 0 }}
              onRefresh={handleRefreshSection}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      )}
    </div>
  );
} 